import { useEffect, useState } from "react";
import { useHelpCentreConfig } from "~/services/queries/variable.queries";
import { IArticleProps, ISectionProps } from "~/lib/types/article.type";
import {
  LocaleLink,
  useLocale,
  usePathnameWithoutLocale,
} from "~/lib/components/link";
import { useGetSytemState } from "~/services/queries/maintennace.queries";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import { getTranslationVariable } from "~/lib/utils/helper/getVariableTranslation";
import { useLocation, useSearchParams } from "@remix-run/react";

interface IPageProps {
  article?: IArticleProps;
}

const Breadcrumbs = ({ article }: IPageProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locale = useLocale();
  const [breadcrumbs, setBreadcrumbs] = useState<object[]>();
  const [isMaintenance, setIsMaintenance] = useState<boolean>(false);
  const { data: helpCentreConfig } = useHelpCentreConfig();
  const { data: systemState, isLoading: isLoadingState } = useGetSytemState();
  const { t } = useTranslation();
  const isTransactionPage = location.pathname.includes("transaction");
  const isArticlePage = location.pathname.includes("article");

  useEffect(() => {
    if (
      systemState &&
      (systemState?.malaysia?.android == true ||
        systemState?.malaysia?.ios == true)
    ) {
      setIsMaintenance(true);
    }
  }, [systemState]);

  useEffect(() => {
    let linkPath: object[] = [];
    const currentPage = location.pathname?.split(/[/, ?]/)[1];
    if (!currentPage) {
      return;
    }
    const pagesToBreadscrumsName = {
      feedback: t("shareYourFeedback"),
      "chat-history": t("chatHistoryTitle"),
      "chat-with-us": t("chatWithUs"),
      "email-us": t("emailUs"),
      "submit-receipts": t("submitReceipts"),
      "mobile-search": "Mobile search",
      search: t("search"),
      "tutorial-videos": t("tutorialVideos"),
      emadani: t("appealEmadaniClaim"),
    };

    switch (currentPage) {
      case "features":
        const currentTopic = helpCentreConfig?.TOPICS?.find(
          (topic: ISectionProps) =>
            topic.sectionId.toString() === searchParams.get("id")
        );
        const currentSubsection = currentTopic?.subSections
          .filter((subsection) => subsection.isFeatureList)
          .find((subsection) => subsection.value === searchParams.get("label"));

        if (currentTopic && currentSubsection) {
          const urlTopic = `/features/${currentTopic.sectionId}/${currentSubsection.value}`;
          linkPath.push({
            breadcrumb: getTranslationVariable(currentSubsection.label, locale),
            href: urlTopic,
          });
        }

        break;
      case "topics": {
        const currentTopic = helpCentreConfig?.TOPICS?.find(
          (topic: ISectionProps) =>
            topic.sectionId.toString() === searchParams.get("id")
        );
        // subSection has 2 types: isFeatureList and not isFeatureList
        const currentSubsection = currentTopic?.subSections
          .filter((subsection) => !subsection.isFeatureList) // not isFeatureList
          .find((subsection) => subsection.value === searchParams.get("label"));

        if (currentTopic && currentSubsection) {
          const urlTopic = `/topics/${currentTopic.sectionId}/${currentSubsection.value}`;
          linkPath.push({
            breadcrumb: getTranslationVariable(currentSubsection.label, locale),
            href: urlTopic,
          });
        }

        const currentSubsectionFeatured = currentTopic?.subSections
          .filter((subsection) => subsection.isFeatureList) // isFeatureList
          .find((subsection) =>
            subsection.articles
              ?.map((x) => x.value.toLowerCase())
              .includes(
                searchParams.get("label")?.toString()?.toLowerCase() || ""
              )
          );

        if (currentTopic && currentSubsectionFeatured) {
          const urlFeature = `/features/${currentTopic.sectionId}/${currentSubsectionFeatured.value}`;

          linkPath.push({
            breadcrumb: getTranslationVariable(
              currentSubsectionFeatured.label,
              locale
            ),
            href: urlFeature,
          });

          const currentSubSubsectionFeatured =
            currentSubsectionFeatured.articles?.find(
              (article) => article.value === searchParams.get("label")
            );

          if (currentSubSubsectionFeatured) {
            const urlTopic = `/topics/${currentTopic.sectionId}/${currentSubSubsectionFeatured.value}`;
            linkPath.push({
              breadcrumb: getTranslationVariable(
                currentSubsectionFeatured.label,
                locale
              ),
              href: urlTopic,
            });
          }
        }
        break;
      }
      case "article": {
        const currentTopic = helpCentreConfig?.TOPICS?.find(
          (topic: ISectionProps) =>
            topic.sectionId.toString() === article?.section_id.toString()
        );

        const currentSubsection = currentTopic?.subSections
          .filter((subsection) => !subsection.isFeatureList)
          .find((subsection) =>
            article?.label_names
              .map((x) => x?.toLowerCase())
              .includes(subsection.value.toLowerCase())
          );

        if (currentTopic && currentTopic) {
          if (currentSubsection) {
            const urlTopic = `/topics/${currentTopic.sectionId}/${currentSubsection?.value}`;
            linkPath.push({
              breadcrumb: getTranslationVariable(
                currentSubsection.label,
                locale
              ),
              href: urlTopic,
            });
          }
        }

        const currentSubsectionFeatured = currentTopic?.subSections
          .filter((subsection) => subsection.isFeatureList) // isFeatureList
          .find((subsection) =>
            subsection.articles?.find((a) =>
              article?.label_names
                ?.map((l) => l.toLowerCase())
                .includes(a.value.toLowerCase())
            )
          );

        if (currentTopic && currentSubsectionFeatured) {
          const urlFeature = `/features/${currentTopic.sectionId}/${currentSubsectionFeatured.value}`;
          linkPath.push({
            breadcrumb: getTranslationVariable(
              currentSubsectionFeatured.label,
              locale
            ),
            href: urlFeature,
          });
          const currentSubSubsectionFeatured =
            currentSubsectionFeatured.articles?.find((a) =>
              article?.label_names
                ?.map((l) => l.toLowerCase())
                .includes(a.value.toLowerCase())
            );
          if (currentSubSubsectionFeatured) {
            const urlTopic = `/topics/${currentTopic.sectionId}/${currentSubSubsectionFeatured.value}`;

            linkPath.push({
              breadcrumb: getTranslationVariable(
                currentSubSubsectionFeatured.label,
                locale
              ),
              href: urlTopic,
            });
          }
        }

        linkPath.push({
          breadcrumb: article?.title,
          href: location.pathname,
        });
        break;
      }
      case "transaction": {
        linkPath.push({
          breadcrumb: t("allTransactions"),
          href: "/transaction",
        });
        if (location.pathname.includes("id")) {
          linkPath.push({
            breadcrumb: t("transactionDetails"),
            href: location.pathname,
          });
        }
        break;
      }
      default: {
        linkPath.push({
          breadcrumb: pagesToBreadscrumsName[currentPage],
          href: location.pathname,
        });
        break;
      }
    }

    setBreadcrumbs(linkPath);
  }, [location, searchParams, locale, helpCentreConfig, article, t]);

  const pathnameWithoutLocale = usePathnameWithoutLocale();

  if (breadcrumbs?.length === 0) {
    return null;
  }

  return (
    <div
      className={`my-5 p-0 px-5 lg:px-0 truncate w-full max-w-screen-md md:max-w-8xl text-xs font-normal leading-[18px] text-neutral-400 md:py-[5px]
    ${
      pathnameWithoutLocale !== "/submit-receipts"
        ? "block"
        : isMaintenance
        ? "block"
        : "hidden"
    } ${isTransactionPage || isArticlePage ? "md:mt-0" : ""}`}
    >
      <LocaleLink to={"/"} className="hover:text-brand-700 hover:underline">
        Setel Help
      </LocaleLink>
      {breadcrumbs?.map((breadcrumb: any, i: any) => {
        return (
          <span key={i}>
            <span className="mx-2 text-xs text-neutral-400 leading-[18px]">
              /
            </span>
            <LocaleLink
              to={breadcrumb?.href}
              className={
                breadcrumb?.href === location.pathname
                  ? "pointer-events-none"
                  : "hover:text-brand-700 hover:underline"
              }
            >
              {breadcrumb?.breadcrumb}
            </LocaleLink>
          </span>
        );
      })}
    </div>
  );
};

export default Breadcrumbs;
